import * as XLSX from 'xlsx';
import { TIPOS_PRODUCTO_DEFAULT } from './tiposProducto';
import { NOMBRES as FICHA_NOMBRES } from './fichaEstado';
import { esCorreoSintetico } from './correoCliente';
import { fmtFechaCorta } from './colTimeUtils';

// V2c: fecha para exportaciones SIEMPRE en hora Colombia (DD/MM/YYYY). Sin esto, una fecha-solo
// (última compra del perfil) se corría al día anterior en zonas al oeste de Bogotá.
const fmtFechaExport = (d) => (d ? fmtFechaCorta(d, { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/—/, '') : '');

// U1: en un export (archivo de datos), el correo sintético del POS queda VACÍO
// (no «Sin correo»: una columna «Correo» con texto ensuciaría un mail-merge).
const correoParaExport = (email) => (esCorreoSintetico(email) ? '' : (email || ''));

export function exportCustomersToExcel(customers, filename = 'clientes.xlsx') {
  const fmtDate = fmtFechaExport; // V2c: hora Colombia (evita el corrimiento de día)

  const rows = customers.map(c => ({
    Nombre:               c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || '—',
    Cédula:               c.cedula || 'No registrada',
    Correo:               correoParaExport(c.email),
    Teléfono:             c.phone || '',
    Origen:               c.origen === 'pos' ? 'POS' : c.origen === 'web' ? 'Web' : '',
    Pedidos:              parseInt(c.orders_count) || 0,
    'Total gastado':      parseFloat(c.total_spent) || 0,
    'Ticket promedio':    parseFloat(c.avg_order_value) || 0,
    'Última compra':      fmtDate(c.date_last_order || c.date_last_active),
    'Fecha registro':     fmtDate(c.date_registered),
  }));

  const ws = XLSX.utils.json_to_sheet(rows);
  ws['!cols'] = [
    { wch: 30 }, { wch: 16 }, { wch: 32 }, { wch: 18 },
    { wch: 10 }, { wch: 10 }, { wch: 16 }, { wch: 16 },
    { wch: 16 }, { wch: 16 },
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Clientes');
  XLSX.writeFile(wb, filename);
}

export function exportSegmentToExcel(customers, segmentLabel, filename) {
  const fmtDate = fmtFechaExport; // V2c: hora Colombia (evita el corrimiento de día)

  const rows = customers.map(c => ({
    Nombre:           c.name || '—',
    Cédula:           c.cedula || 'No registrada',
    Correo:           correoParaExport(c.email),
    Teléfono:         c.phone || '',
    Origen:           c.origen === 'pos' ? 'POS' : c.origen === 'web' ? 'Web' : '',
    Pedidos:          parseInt(c.orders_count) || 0,
    'Total gastado':  parseFloat(c.total_spent) || 0,
    'Última compra':  fmtDate(c.date_last_order),
  }));

  const ws = XLSX.utils.json_to_sheet(rows);
  ws['!cols'] = [
    { wch: 30 }, { wch: 16 }, { wch: 32 }, { wch: 18 },
    { wch: 10 }, { wch: 10 }, { wch: 16 }, { wch: 16 },
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, segmentLabel.slice(0, 31));
  XLSX.writeFile(wb, filename || `segmento-${segmentLabel.toLowerCase().replace(/\s+/g, '-')}.xlsx`);
}

export function exportNotifyToExcel(customers, category, filename) {
  const rows = customers.map(c => ({
    Nombre:          c.name || '—',
    Cédula:          c.cedula || 'No registrada',
    Teléfono:        c.phone || '',
    Correo:          correoParaExport(c.email),
    'Veces compró':  c.times_bought || 0,
    Pedidos:         parseInt(c.orders_count) || 0,
    'Total gastado': parseFloat(c.total_spent) || 0,
  }));

  const ws = XLSX.utils.json_to_sheet(rows);
  ws['!cols'] = [
    { wch: 30 }, { wch: 16 }, { wch: 18 }, { wch: 32 },
    { wch: 14 }, { wch: 10 }, { wch: 16 },
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, category.slice(0, 31));
  XLSX.writeFile(wb, filename || `avisar-${category.toLowerCase().replace(/\s+/g, '-')}.xlsx`);
}

function formatPrice(price) {
  const n = parseFloat(price);
  return isNaN(n) ? '' : n;
}

// Márgenes — costos y márgenes por producto (client-side; `rows` = filas ya
// filtradas de MarginsTab). Precio/costo pueden ser rango (min–max) en variables.
export function exportMarginsToExcel(rows, filename = 'margenes.xlsx') {
  const cop = (v) => { const n = parseFloat(v); return isNaN(n) ? '' : `$${n.toLocaleString('es-CO', { maximumFractionDigits: 0 })}`; };
  const precio = (pr) => (pr.single ? cop(pr.single.price) : pr.priceMin === pr.priceMax ? cop(pr.priceMin) : `${cop(pr.priceMin)} – ${cop(pr.priceMax)}`);
  const costo = (pr) => {
    if (!pr.hasAnyCost) return 'Sin costo';
    if (pr.single) return pr.single.cost == null ? 'Sin costo' : cop(pr.single.cost);
    return pr.costMin === pr.costMax ? cop(pr.costMin) : `${cop(pr.costMin)} – ${cop(pr.costMax)}`;
  };
  const out = rows.map((pr) => ({
    'Producto':     pr.productName || '',
    'Categoría':    pr.category || '',
    'Precio venta': precio(pr),
    'Costo unit.':  costo(pr),
    'Ganancia':     pr.avgProfit == null ? '' : (parseFloat(pr.avgProfit) || 0),
    'Margen %':     pr.avgMargin == null ? '' : parseFloat(pr.avgMargin.toFixed(1)),
    'Stock':        pr.totalStock ?? 0,
    'Variaciones':  pr.varCount ?? 1,
  }));
  const ws = XLSX.utils.json_to_sheet(out);
  ws['!cols'] = [{ wch: 34 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 14 }, { wch: 10 }, { wch: 8 }, { wch: 12 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Márgenes');
  XLSX.writeFile(wb, filename);
}

// Proveedores — maestro (client-side; `suppliers` ya viene filtrado en memoria).
export function exportSuppliersToExcel(suppliers, filename = 'proveedores.xlsx') {
  const rows = suppliers.map((s) => ({
    'Nombre':    s.name || '',
    'Contacto':  s.contactName || '',
    'Teléfono':  s.phone || '',
    'Correo':    s.email || '',
    'Ciudad':    s.city || '',
    'País':      s.country || '',
    'Estado':    s.active !== false ? 'Activo' : 'Inactivo',
    'Notas':     s.notes || '',
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  ws['!cols'] = [{ wch: 28 }, { wch: 22 }, { wch: 16 }, { wch: 28 }, { wch: 16 }, { wch: 14 }, { wch: 10 }, { wch: 40 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Proveedores');
  XLSX.writeFile(wb, filename);
}

// Compras — listado de pedidos a proveedores (client-side; `purchases` ya viene
// filtrado del backend). Las etiquetas de estado/pago replican statusMeta/payMeta
// de CostsPage (3 estados de pedido, 3 de pago).
export function exportPurchasesToExcel(purchases, filename = 'compras.xlsx') {
  const fmtDate = fmtFechaExport; // V2c: hora Colombia
  const estado = (s) => (s === 'cancelled' ? 'Cancelado' : s === 'received' ? 'Recibido' : 'Pendiente');
  const pago = (f) => (f === 'paid' ? 'Pagado' : f === 'partially_paid' ? 'Pago parcial' : 'Debe total');

  const rows = purchases.map((p) => ({
    'Pedido':            p.purchaseNumber || '',
    'Proveedor':         p.supplierName || '',
    'Recepción':         estado(p.status),
    'Pago':              p.esHistorico ? '' : pago(p.financialStatus),
    'Fecha':             fmtDate(p.orderDate),
    'Entrega estimada':  fmtDate(p.expectedArrivalDate),
    'Variaciones':       p.lines?.length || 0,
    'Total':             parseFloat(p.totalAmount) || 0,
    'Saldo':             p.esHistorico ? '' : (parseFloat(p.balance) || 0),
    'Histórico':         p.esHistorico ? 'Sí' : '',
  }));

  const ws = XLSX.utils.json_to_sheet(rows);
  ws['!cols'] = [
    { wch: 14 }, { wch: 28 }, { wch: 12 }, { wch: 13 }, { wch: 12 },
    { wch: 16 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 10 },
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Compras');
  XLSX.writeFile(wb, filename);
}

// Tanda 6 (PARTE D) — armado de filas SEPARADO del XLSX para poder testearlo.
// El importador de Excel NO se toca en esta tanda (registrado): esto solo
// EXPORTA. `tipos` traduce el arquetipo a nombre de tipo; NOMBRES de
// fichaEstado traduce las pendientes materializadas.
export function filasCatalogoExcel(products, tipos = TIPOS_PRODUCTO_DEFAULT) {
  const rows = [];
  const estadoLabel = { publish: 'Publicado', draft: 'Borrador', private: 'Privado', pending: 'Pendiente' };
  const textoPlano = (s) => String(s || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  for (const p of products) {
    const tipo = (tipos || []).find((t) => t.arquetipo === p.arquetipo) || (tipos || [])[0] || null;
    const dims = p.dimensions || {};
    const base = {
      Producto: p.name,
      SKU: p.sku || '',
      'Código de barras': p.gtin || '',
      Precio: formatPrice(p.regular_price || p.price),
      'Precio rebajado': p.sale_price ? formatPrice(p.sale_price) : '',
      Cantidad: p.stock_quantity ?? 0,
      'Peso (kg)': p.weight || '',
      'Dimensiones (cm)': [dims.length, dims.width, dims.height].filter(Boolean).join(' × '),
      Categorías: (p.categories || []).join(', '),
      Marca: (p.brands || []).join(', '),
      'Descripción corta': textoPlano(p.short_description),
      'Descripción larga': p.description_text || '',
      'Título SEO': p.seo_title || '',
      'Meta description': p.seo_metadesc || '',
      Fotos: p.images_count ?? 0,
      'Estado en Woo': estadoLabel[p.status] || p.status || '',
      'Tipo de producto': tipo ? tipo.nombre : '',
      // Materializado: sin evaluación va VACÍO, nunca un número inventado.
      Ficha: p.ficha && p.ficha.aplicables ? `${p.ficha.cumplidas}/${p.ficha.aplicables}` : '',
      Pendientes: p.ficha ? (p.ficha.pendientesIds || []).map((id) => FICHA_NOMBRES[id] || id).join('; ') : '',
    };
    if (p.variations && p.variations.length > 0) {
      for (const v of p.variations) {
        rows.push({ ...base, Variación: v.label, Cantidad: v.stock_quantity ?? 0, Precio: formatPrice(v.regular_price || v.price) });
      }
    } else {
      rows.push({ ...base, Variación: '' });
    }
  }
  return rows;
}

export function exportCatalogToExcel(products, filename = 'catalogo.xlsx', tipos = TIPOS_PRODUCTO_DEFAULT) {
  const rows = filasCatalogoExcel(products, tipos);

  const ws = XLSX.utils.json_to_sheet(rows);

  // Anchos de columna aproximados
  ws['!cols'] = [
    { wch: 45 }, // Producto
    { wch: 20 }, // Categoría
    { wch: 35 }, // Variación
    { wch: 12 }, // Cantidad
    { wch: 14 }, // Precio
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Catálogo');
  XLSX.writeFile(wb, filename);
}

// Previsualización de una campaña de promoción → Excel (misma tabla que la
// pantalla). `filas` es el `filas` que devuelve POST /campanas/previsualizar.
export function exportCampanaPreviewToExcel(filas, filename = 'campana-previsualizacion.xlsx') {
  const rows = (filas || []).map((f) => ({
    Producto: f.producto || `#${f.variation_id}`,
    Variación: f.variacion || '',
    Categoría: f.categoria || '',
    'Precio actual': f.precio_actual != null ? f.precio_actual : '',
    'Precio regular': f.precio_regular != null ? f.precio_regular : '',
    'Precio promoción': f.precio_promocion != null ? f.precio_promocion : '',
    '% real': f.pct_real != null ? f.pct_real : '',
    Alertas: (f.alertas || []).join(' · '),
    'No se aplica': f.bloqueante ? 'sí' : '',
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  ws['!cols'] = [{ wch: 30 }, { wch: 20 }, { wch: 24 }, { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 8 }, { wch: 32 }, { wch: 12 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Campaña');
  XLSX.writeFile(wb, filename);
}
